/**
 * UserAuditAdmin.tsx — Admin › Users › User Audit
 * (docs/plans/2026-10-15-001-feat-user-audit-plan.md, U12).
 *
 * What staff did in this Account, for a date range, with a download of the
 * whole filtered list. Gated twice: RequireAdmin (level 3) and the User Audit
 * permission in AdminPage; the server checks both again, live, on every list
 * and download, and answers a refusal with a 422 carrying a plain message.
 *
 * The decisions — which ranges may be asked for, what is sent, which notice
 * shows — live in userAudit.logic.ts so they can be tested without this tree.
 *
 * Shell copied from SupportTicketsAdmin.tsx: ListPageLayout + FilterSidebar,
 * with p-6 on the content pane that encloses PageHeader (ui/CLAUDE.md).
 */
import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { listUsers } from '@/services/admin.service';
import { isDateRange, type FilterConfig, type FilterOption, type FilterValue } from '@/types/filters.types';
import {
  NEVER_RECORDED_LINE,
  PER_PAGE,
  buildExportFilters,
  buildListParams,
  checkDateRange,
  describeLoadFailure,
  digitsOnly,
  formatAuditTime,
  notRecordedNotice,
  staffFilterOptions,
  staffFilterParam,
  type UserAuditFilters,
} from '@/pages/admin/userAudit.logic';
import { listUserAudit, type UserAuditRow } from '@/pages/admin/userAudit.service';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'record', label: 'Search', type: 'search', allLabel: 'Patient, plan or other number…' },
  // Required: clearing it returns it to the last 7 days rather than to all time.
  { key: 'daterange', label: 'Date', type: 'daterange', defaultPreset: '7days' },
  { key: 'userId', label: 'Staff', type: 'select', allLabel: 'All staff', searchable: true },
];

export function UserAuditAdmin() {
  // No storage key: an audit opens on the last 7 days every time, not on
  // whatever someone looked at last week.
  const filterState = useLocalDashboardFilters(FILTER_CONFIGS);
  const filterValues = filterState.values;
  // The page belongs to the filters it was chosen under. Resetting it in an
  // effect would render once with the new filters and the old page, and ask
  // for that page; deriving it means a filter change asks for page 1 only.
  const [pageState, setPageState] = useState({ key: '', page: 1 });

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'user-audit',
    entityLabel: 'activity entries',
  });

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });

  const filterOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({ userId: staffFilterOptions(usersQuery.data ?? []) }),
    [usersQuery.data],
  );

  const range = filterValues.daterange;
  const filters = useMemo<UserAuditFilters>(() => ({
    dateFrom: isDateRange(range) ? range.from : '',
    dateTo: isDateRange(range) ? range.to : '',
    userId: staffFilterParam(filterValues.userId as string[] | undefined, filterOptions.userId),
    record: digitsOnly((filterValues.record as string[] | undefined)?.[0] ?? ''),
  }), [range, filterValues.userId, filterValues.record, filterOptions.userId]);

  const rangeCheck = checkDateRange(filters.dateFrom, filters.dateTo);
  const filterKey = JSON.stringify(filters);
  const page = pageState.key === filterKey ? pageState.page : 1;
  const setPage = useCallback((next: number) => setPageState({ key: filterKey, page: next }), [filterKey]);

  const listQuery = useQuery({
    queryKey: ['user-audit', filterKey, page],
    queryFn: () => listUserAudit(buildListParams(filters, page)),
    enabled: rangeCheck.ok,
    placeholderData: keepPreviousData,
    retry: false,
  });

  // The number search takes digits only; anything else typed is dropped.
  const onFilterChange = useCallback((key: string, value: FilterValue) => {
    if (key === 'record' && Array.isArray(value)) {
      const digits = digitsOnly(value[0] ?? '');
      filterState.setFilter(key, digits ? [digits] : []);
      return;
    }
    filterState.setFilter(key, value);
  }, [filterState]);

  const rows = rangeCheck.ok ? listQuery.data?.data ?? [] : [];
  const meta = rangeCheck.ok ? listQuery.data?.meta : undefined;
  const total = meta?.total ?? 0;
  const notice = meta ? notRecordedNotice(meta.recordingStartedAt, filters.dateFrom) : null;
  const failure = rangeCheck.ok && listQuery.isError && !listQuery.isFetching ? describeLoadFailure(listQuery.error) : null;

  const columns = useMemo<DataTableColumn<UserAuditRow>[]>(() => [
    {
      key: 'occurredAt',
      header: 'Time',
      width: '210px',
      // Each row carries its own zone: a range that crosses a clock change
      // holds both, so one label in the header would be wrong for some rows.
      render: (row) => (
        <span className="whitespace-nowrap text-slate-600">
          <span>{formatAuditTime(row.occurredAt)}</span>
          {row.timeZone && <span className="ml-1 text-slate-400">{row.timeZone}</span>}
        </span>
      ),
    },
    {
      key: 'staffName',
      header: 'Staff',
      render: (row) => (
        <div>
          <div className="text-slate-900">{row.staffName}</div>
          {row.corporateAdminName && (
            <div className="text-xs text-slate-500">Corporate admin: {row.corporateAdminName}</div>
          )}
        </div>
      ),
    },
    { key: 'activity', header: 'Activity', width: '200px' },
    { key: 'description', header: 'Description' },
  ], []);

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={filterValues}
          options={filterOptions}
          onChange={onFilterChange}
          onClear={filterState.clearFilter}
          showAIAssistant={false}
          defaultCollapsed={false}
          storageKey="user-audit-admin"
          searchCommitMode="blur-or-enter"
        />
      )}
    >
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <PageHeader
          title="User Audit"
          actions={(
            <>
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={() => startExport(buildExportFilters(filters))}
                disabled={!rangeCheck.ok || total === 0 || exporting}
                loading={exporting}
              >
                Export
              </Button>
              {exportModal}
            </>
          )}
        />

        <div className="space-y-1 text-sm text-slate-500">
          <p>{NEVER_RECORDED_LINE}</p>
          {notice && <p className="text-amber-700">{notice}</p>}
        </div>

        {!rangeCheck.ok ? (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
            {rangeCheck.message}
          </div>
        ) : failure ? (
          <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-600">
            <p>{failure.message}</p>
            {failure.canRetry && (
              <Button className="mt-3" variant="secondary" size="sm" onClick={() => void listQuery.refetch()}>
                Try again
              </Button>
            )}
          </div>
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            keyField="id"
            loading={listQuery.isFetching}
            page={page}
            pageSize={PER_PAGE}
            total={total}
            onPageChange={setPage}
            emptyTitle="No activity found"
            emptyDescription="Try a wider date range, all staff, or a different number."
          />
        )}
      </div>
    </ListPageLayout>
  );
}
